import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../layouts/header';
import Footer from '../../layouts/footer';
import '../../../cssFiles/reports.scss';
import { leadServiceHelper } from '../../../services/leadServices';
import { REPORTS, getReportById } from './reportsData';
import PMSReport from './PMSReport';
import MarketPEReport from './MarketPEReport';

const REPORT_VIEWS = {
  'advanced-pms': PMSReport,
  'market-pe-valuation': MarketPEReport,
};

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const FileIcon = () => (
  <svg className="rpt-sticky-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="12" y1="18" x2="12" y2="12" />
    <polyline points="9 15 12 18 15 15" />
  </svg>
);

const ReportPage = () => {
  // ── View state ──────────────────────────────────────────────────────────────
  const [activeId, setActiveId] = useState(() =>
    getReportById(window.location.hash.replace('#', '')) ? window.location.hash.replace('#', '') : null
  );

  // ── Modal state ─────────────────────────────────────────────────────────────
  const [modalReport, setModalReport] = useState(null);
  const [userInput, setUserInput] = useState('');
  const [showLink, setShowLink] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const activeReport = activeId ? getReportById(activeId) : null;
  const ActiveView = activeId ? REPORT_VIEWS[activeId] : null;

  // Keep the browser back button working with the hash.
  useEffect(() => {
    const onHashChange = () => {
      const id = window.location.hash.replace('#', '');
      setActiveId(getReportById(id) ? id : null);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const openReport = useCallback((id) => {
    window.location.hash = id;
    setActiveId(id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const backToList = useCallback(() => {
    window.location.hash = '';
    setActiveId(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // ── Download flow ───────────────────────────────────────────────────────────
  const openDownloadModal = (report) => {
    setModalReport(report);
    setShowLink(false);
    setUserInput('');
    setError('');
    setLoading(false);
  };

  const closeModal = () => {
    setModalReport(null);
    setShowLink(false);
    setUserInput('');
    setError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const mobileRegex = /^[0-9]{10}$/;
    if (!emailRegex.test(userInput) && !mobileRegex.test(userInput)) {
      setError('Enter a valid email or 10-digit mobile number.');
      return;
    }
    const isEmail = emailRegex.test(userInput);
    const form = {
      name: '',
      mobile: isEmail ? '' : userInput,
      email: isEmail ? userInput : '',
      source: `Report - ${modalReport ? modalReport.id : ''}`,
    };
    setLoading(true);
    leadServiceHelper.submitMobileNumberService(form, () => {
      setShowLink(true);
      setError('');
      setLoading(false);
    });
  };

  return (
    <>
      <Header />

      {/* Sticky download bar — only on a detail view */}
      {activeReport && (
        <div className="rpt-sticky-bar">
          <div className="rpt-sticky-inner">
            <button className="rpt-back-btn" onClick={backToList} aria-label="Back to all reports">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              <span>All reports</span>
            </button>
            <span className="rpt-sticky-label">
              <FileIcon />
              <span className="rpt-sticky-text">{activeReport.stickyNote}</span>
            </span>
            <button className="rpt-download-btn" onClick={() => openDownloadModal(activeReport)}>
              <DownloadIcon />
              Download PDF
            </button>
          </div>
        </div>
      )}

      {/* ── List view ─────────────────────────────────────────────────────── */}
      {!activeReport && (
        <div className="rpt-list-wrapper">
          <div className="rpt-list-inner">
            <header className="rpt-list-hero">
              <p className="rpt-list-eyebrow">Research library</p>
              <h1 className="rpt-list-title">Reports worth reading before you invest</h1>
              <p className="rpt-list-sub">
                Independent, data-led research on Indian markets and investment products.
                Read online, or download the PDF.
              </p>
            </header>

            <div className="rpt-card-grid">
              {REPORTS.map((report) => (
                <article className="rpt-card" key={report.id}>
                  <div className="rpt-card-body">
                    <span className="rpt-card-tag">{report.tag}</span>
                    <h2 className="rpt-card-title">{report.title}</h2>
                    <p className="rpt-card-sub">{report.subtitle}</p>
                    <p className="rpt-card-desc">{report.description}</p>
                    <ul className="rpt-card-points">
                      {report.highlights.map((h) => (
                        <li key={h}>{h}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="rpt-card-footer">
                    <span className="rpt-card-meta">{report.meta}</span>
                    <div className="rpt-card-actions">
                      <button className="rpt-btn-ghost" onClick={() => openDownloadModal(report)}>
                        <DownloadIcon />
                        PDF
                      </button>
                      <button className="rpt-btn-primary" onClick={() => openReport(report.id)}>
                        Read report
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Detail view ───────────────────────────────────────────────────── */}
      {activeReport && ActiveView && (
        <div className="rpt-report-wrapper">
          <div className="rpt-report-container">
            <ActiveView />
          </div>
          <div className="rpt-report-footer-actions">
            <button className="rpt-btn-ghost" onClick={backToList}>Back to all reports</button>
            <button className="rpt-btn-primary" onClick={() => openDownloadModal(activeReport)}>
              <DownloadIcon />
              Download PDF
            </button>
          </div>
        </div>
      )}

      {/* ── Lead-capture modal (shared by both reports) ───────────────────── */}
      {modalReport && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={closeModal} aria-label="Close">&times;</button>
            <h4>{!showLink ? 'Enter email or mobile to download' : 'Your download link is ready'}</h4>
            <p className="modal-report-name">{modalReport.title}</p>
            {!showLink ? (
              loading ? (
                <div className="loader-container">
                  <div className="loader"></div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="download-form">
                  <input
                    type="text"
                    placeholder="Email or mobile number"
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    required
                  />
                  {error && <div className="error-msg">{error}</div>}
                  <button type="submit" className="submit-btn">Get link</button>
                </form>
              )
            ) : (
              <div className="download-link-section">
                <a
                  href={modalReport.driveLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="drive-link"
                >
                  Download the report
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default ReportPage;
